import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Job, JobPriority, JobStatus, JobType, User } from '../types/job';

interface UserWithRoles {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface JobFormData {
  job_code: string;
  job_type: JobType | null;
  status: JobStatus;
  priority: JobPriority;
  assigned_driver_id: string;
  assigned_supervisor_id: string;
  planned_start_at: string;
  planned_end_at: string;
  estimated_duration_hours: string;
  notes: string;
}

const EditJob: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [formData, setFormData] = useState<JobFormData>({
    job_code: '',
    job_type: null,
    status: JobStatus.DRAFT,
    priority: JobPriority.NORMAL,
    assigned_driver_id: '',
    assigned_supervisor_id: '',
    planned_start_at: '',
    planned_end_at: '',
    estimated_duration_hours: '',
    notes: '',
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadJobData();
    loadUsers();
  }, [id]);

  const loadJobData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/jobs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        const jobData = data.data;
        setJob(jobData);
        setFormData({
          job_code: jobData.job_code || '',
          job_type: jobData.job_type,
          status: jobData.status,
          priority: jobData.priority,
          assigned_driver_id: jobData.assigned_driver_id || '',
          assigned_supervisor_id: jobData.assigned_supervisor_id || '',
          planned_start_at: jobData.planned_start_at ? jobData.planned_start_at.split('.')[0] : '',
          planned_end_at: jobData.planned_end_at ? jobData.planned_end_at.split('.')[0] : '',
          estimated_duration_hours: jobData.estimated_duration_hours?.toString() || '',
          notes: jobData.notes || '',
        });
      }
    } catch (err) {
      console.error('Error loading job:', err);
      alert('Failed to load job data');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const [driversResponse, supervisorsResponse] = await Promise.all([
        fetch('/api/users?role=driver', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch('/api/users?role=supervisor', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (driversResponse.ok) {
        const driversData = await driversResponse.json();
        setDrivers(driversData.users || []);
      }

      if (supervisorsResponse.ok) {
        const supervisorsData = await supervisorsResponse.json();
        setSupervisors(supervisorsData.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');

      const payload = {
        job_code: formData.job_code.trim() || null,
        job_type: formData.job_type,
        status: formData.status,
        priority: formData.priority,
        assigned_driver_id: formData.assigned_driver_id || null,
        assigned_supervisor_id: formData.assigned_supervisor_id || null,
        planned_start_at: formData.planned_start_at || null,
        planned_end_at: formData.planned_end_at || null,
        estimated_duration_hours: formData.estimated_duration_hours ? parseFloat(formData.estimated_duration_hours) : null,
        notes: formData.notes || null,
      };

      const response = await fetch(`/api/jobs/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to update job');
      }

      navigate(`/jobs/${id}`);
    } catch (error) {
      console.error('Error updating job:', error);
      alert(error instanceof Error ? error.message : 'Failed to update job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: keyof JobFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatJobType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading job...</div>
        </main>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Job not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(`/jobs/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Job Details
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">
              Edit Job {job.job_number}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="job_code">Job Code</Label>
                <Input
                  id="job_code"
                  placeholder="Enter a manual job code (optional)"
                  value={formData.job_code}
                  onChange={(e) => handleChange('job_code', e.target.value)}
                />
              </div>

              {/* Job Type and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="job_type">Job Type</Label>
                  <Select
                    value={formData.job_type || 'none'}
                    onValueChange={(value) => handleChange('job_type', value === 'none' ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Type (Task-Based)</SelectItem>
                      <SelectItem value={JobType.DELIVERY}>Delivery</SelectItem>
                      <SelectItem value={JobType.PICKUP}>Pickup</SelectItem>
                      <SelectItem value={JobType.INSTALLATION}>Installation</SelectItem>
                      <SelectItem value={JobType.MAINTENANCE}>Maintenance</SelectItem>
                      <SelectItem value={JobType.TRANSFER}>Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priority *</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => handleChange('priority', value as JobPriority)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={JobPriority.LOW}>Low</SelectItem>
                      <SelectItem value={JobPriority.NORMAL}>Normal</SelectItem>
                      <SelectItem value={JobPriority.HIGH}>High</SelectItem>
                      <SelectItem value={JobPriority.URGENT}>Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Status */}
              <div>
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange('status', value as JobStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={JobStatus.DRAFT}>Draft</SelectItem>
                    <SelectItem value={JobStatus.ASSIGNED}>Assigned</SelectItem>
                    <SelectItem value={JobStatus.IN_PROGRESS}>In Progress</SelectItem>
                    <SelectItem value={JobStatus.COMPLETED}>Completed</SelectItem>
                    <SelectItem value={JobStatus.ON_HOLD}>On Hold</SelectItem>
                    <SelectItem value={JobStatus.CANCELLED}>Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assignment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assigned_driver_id">Assign Driver</Label>
                  <Select
                    value={formData.assigned_driver_id || 'unassigned'}
                    onValueChange={(value) => handleChange('assigned_driver_id', value === 'unassigned' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select driver (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {drivers.map((driver) => (
                        <SelectItem key={driver.id} value={driver.id}>
                          {driver.first_name} {driver.last_name} ({driver.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="assigned_supervisor_id">Assign Supervisor</Label>
                  <Select
                    value={formData.assigned_supervisor_id || 'unassigned'}
                    onValueChange={(value) => handleChange('assigned_supervisor_id', value === 'unassigned' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select supervisor (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {supervisors.map((supervisor) => (
                        <SelectItem key={supervisor.id} value={supervisor.id}>
                          {supervisor.first_name} {supervisor.last_name} ({supervisor.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Timing */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="planned_start_at">Planned Start</Label>
                  <Input
                    id="planned_start_at"
                    type="datetime-local"
                    value={formData.planned_start_at}
                    onChange={(e) => handleChange('planned_start_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="planned_end_at">Planned End</Label>
                  <Input
                    id="planned_end_at"
                    type="datetime-local"
                    value={formData.planned_end_at}
                    onChange={(e) => handleChange('planned_end_at', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="estimated_duration_hours">Estimated Duration (hours)</Label>
                  <Input
                    id="estimated_duration_hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_duration_hours}
                    onChange={(e) => handleChange('estimated_duration_hours', e.target.value)}
                    placeholder="e.g., 8"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes & Instructions</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Add any special instructions or notes for this job..."
                  rows={4}
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(`/jobs/${id}`)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Updating...' : 'Update Job'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default EditJob;
